import { useState, useEffect, useCallback } from "react";
import {
  inventoryApi,
  type Model,
  type Product,
} from "../api/inventory";
import "./Inventory.css";

type FormMode = "none" | "addModel" | "editModel" | "addFlavor" | "restock";

export default function Inventory() {
  const [models, setModels] = useState<Model[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Form state
  const [formMode, setFormMode] = useState<FormMode>("none");
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [restockingProduct, setRestockingProduct] = useState<Product | null>(null);

  // Form fields
  const [modelName, setModelName] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [markupAmount, setMarkupAmount] = useState("100");
  const [flavorName, setFlavorName] = useState("");
  const [restockQty, setRestockQty] = useState("");
  const [restockCost, setRestockCost] = useState("");

  const [saving, setSaving] = useState(false);

  // Bulk Upload state
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkResult, setBulkResult] = useState<{
    created?: { models: number; products: number };
    updated?: { models: number; products: number };
    error?: string;
  } | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    const [mRes, pRes] = await Promise.all([
      inventoryApi.getModels(),
      inventoryApi.getProducts(),
    ]);
    if (mRes.error || pRes.error) {
      setError(mRes.error ?? pRes.error ?? "Error loading data");
    } else {
      setModels(mRes.data ?? []);
      setProducts(pRes.data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setFormMode("none");
    setModelName("");
    setCostPrice("");
    setMarkupAmount("100");
    setFlavorName("");
    setRestockQty("");
    setRestockCost("");
    setEditingModel(null);
    setSelectedModelId(null);
    setRestockingProduct(null);
  };

  const getProductsByModel = (modelId: number) =>
    products.filter((p) => p.modelId === modelId);

  const quantityColor = (qty: number) => {
    if (qty > 5) return "qty-green";
    if (qty > 0) return "qty-yellow";
    return "qty-red";
  };

  // ─── Handlers ──────────────────────────────────────

  const handleAddModel = () => {
    resetForm();
    setFormMode("addModel");
  };

  const handleEditModel = (model: Model) => {
    resetForm();
    setEditingModel(model);
    setModelName(model.name);
    setCostPrice(String(model.costPrice));
    setMarkupAmount(String((model as Model & { markupAmount?: number }).markupAmount ?? 100));
    setFormMode("editModel");
  };

  const handleSaveModel = async () => {
    setSaving(true);
    const payload = {
      name: modelName,
      costPrice: Number(costPrice),
      markupAmount: Number(markupAmount),
    };

    const result = formMode === "addModel"
      ? await inventoryApi.createModel(payload)
      : await inventoryApi.updateModel(editingModel!.id, payload);

    if (result.error) {
      setError(result.error);
    } else {
      resetForm();
      await loadData();
    }
    setSaving(false);
  };

  const handleDeleteModel = async (id: number) => {
    if (!confirm("¿Eliminar este modelo y todos sus sabores?")) return;
    await inventoryApi.deleteModel(id);
    await loadData();
  };

  const handleAddFlavor = (modelId: number) => {
    resetForm();
    setSelectedModelId(modelId);
    setFormMode("addFlavor");
  };

  const handleSaveFlavor = async () => {
    if (!selectedModelId || !flavorName.trim()) return;
    setSaving(true);

    const result = await inventoryApi.createProduct({
      modelId: selectedModelId,
      flavor: flavorName.trim(),
    });

    if (result.error) {
      setError(result.error);
    } else {
      resetForm();
      await loadData();
    }
    setSaving(false);
  };

  const handleDeleteFlavor = async (id: number, flavor: string) => {
    if (!confirm(`¿Eliminar el sabor "${flavor}"?`)) return;
    await inventoryApi.deleteProduct(id);
    await loadData();
  };

  const handleRestock = (product: Product) => {
    resetForm();
    setRestockingProduct(product);
    setRestockCost(String(
      models.find((m) => m.id === product.modelId)?.costPrice ?? 0
    ));
    setFormMode("restock");
  };

  const handleSaveRestock = async () => {
    if (!restockingProduct) return;
    setSaving(true);

    const result = await inventoryApi.restock({
      productId: restockingProduct.id,
      quantity: Number(restockQty),
      unitCost: Number(restockCost),
    });

    if (result.error) {
      setError(result.error);
    } else {
      resetForm();
      await loadData();
    }
    setSaving(false);
  };

  const handleBulkUpload = async () => {
    if (!bulkText.trim()) return;
    setBulkSaving(true);
    setBulkResult(null);

    const res = await fetch("/api/inventory/bulk", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: bulkText }),
    });

    const data = await res.json();
    if (res.ok) {
      setBulkResult(data);
      setBulkText("");
      await loadData();
    } else {
      setBulkResult({ error: data.error || "Error processing upload" });
    }
    setBulkSaving(false);
  };

  // ─── Render ────────────────────────────────────────

  if (loading) {
    return (
      <div>
        <div className="screen-header">
          <h1>Inventory</h1>
          <p>Manage models, flavors, and stock</p>
        </div>
        <p className="inv-status">Cargando inventario…</p>
      </div>
    );
  }

  return (
    <div>
      <div className="screen-header">
        <h1>Inventory</h1>
        <p>Manage models, flavors, and stock</p>
      </div>

      {error && (
        <div className="inv-error" onClick={() => setError("")}>
          Error: {error} <span className="inv-error__dismiss">x</span>
        </div>
      )}

      {/* ─── Bulk Upload Button & Panel ──────────── */}
      {formMode === "none" && (
        <button
          className="inv-add-btn"
          style={{ marginBottom: "0.5rem" }}
          onClick={() => { setShowBulk(!showBulk); setBulkResult(null); }}
        >
          <span className="material-symbols-rounded">upload_file</span>{" "}
          {showBulk ? "Ocultar carga masiva" : "Bulk Upload"}
        </button>
      )}

      {showBulk && formMode === "none" && (
        <div className="inv-form" style={{ marginBottom: "1rem" }}>
          <h3 className="inv-form__title">
            <span className="material-symbols-rounded">file_upload</span> Carga masiva de inventario
          </h3>
          <p style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)", marginBottom: "0.75rem" }}>
            Pega el texto en formato: #Modelo → $Costo Margen → Sabor Cantidad
          </p>
          <textarea
            className="inv-input"
            style={{ minHeight: "120px", fontFamily: "monospace", fontSize: "0.82rem" }}
            placeholder={`#Waka 33k\n$235 100\nWatermelon Chill 4\nPeach 2\n\n#SMOK Nord 5\n$250 75\nGrape 3`}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            disabled={bulkSaving}
          />
          <div className="inv-form__actions">
            <button
              type="button"
              className="inv-btn inv-btn--primary"
              onClick={handleBulkUpload}
              disabled={bulkSaving || !bulkText.trim()}
            >
              {bulkSaving ? "Procesando…" : (
                <><span className="material-symbols-rounded">cloud_upload</span> Cargar inventario</>
              )}
            </button>
          </div>

          {bulkResult && !bulkResult.error && (
            <div className="inv-bulk-result" style={{
              marginTop: "0.75rem",
              padding: "0.75rem",
              background: "rgba(16,185,129,0.08)",
              borderRadius: "8px",
              fontSize: "0.85rem",
            }}>
              <span className="material-symbols-rounded" style={{ color: "var(--color-success)" }}>check_circle</span>
              {" "}Creados: {bulkResult.created?.models ?? 0} modelos, {bulkResult.created?.products ?? 0} sabores
              {(bulkResult.updated?.models ?? 0) > 0 || (bulkResult.updated?.products ?? 0) > 0 ? (
                <> | Actualizados: {bulkResult.updated?.models ?? 0} modelos, {bulkResult.updated?.products ?? 0} sabores</>
              ) : null}
            </div>
          )}

          {bulkResult?.error && (
            <div className="inv-error" style={{ marginTop: "0.75rem" }}>
              Error: {bulkResult.error}
            </div>
          )}
        </div>
      )}

      {/* ─── Add Model Button ─────────────────────── */}
      {formMode === "none" && (
        <button className="inv-add-btn" onClick={handleAddModel}>
          + Agregar modelo
        </button>
      )}

      {/* ─── Add / Edit Model Form ────────────────── */}
      {(formMode === "addModel" || formMode === "editModel") && (
        <form
          className="inv-form"
          onSubmit={(e) => { e.preventDefault(); handleSaveModel(); }}
        >
          <h3 className="inv-form__title">
            {formMode === "addModel" ? "Nuevo modelo" : "Editar modelo"}
          </h3>
          <input
            className="inv-input"
            placeholder="Nombre del modelo"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            disabled={saving}
          />
          <div className="inv-form__prices">
            <label>
              <span>Costo ($)</span>
              <input
                className="inv-input"
                type="number"
                placeholder="Precio de costo"
                value={costPrice}
                onChange={(e) => setCostPrice(e.target.value)}
                min={0}
                disabled={saving}
              />
            </label>
            <label>
              <span>Margen ($)</span>
              <input
                className="inv-input"
                type="number"
                placeholder="Margen de ganancia"
                value={markupAmount}
                onChange={(e) => setMarkupAmount(e.target.value)}
                min={0}
                disabled={saving}
              />
            </label>
          </div>
          <div className="inv-form__actions">
            <button type="submit" className="inv-btn inv-btn--primary" disabled={saving}>
              {saving ? "Guardando…" : (
                <><span className="material-symbols-rounded">save</span> Guardar modelo</>
              )}
            </button>
            <button type="button" className="inv-btn inv-btn--ghost" onClick={resetForm}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* ─── Add Flavor Form ──────────────────────── */}
      {formMode === "addFlavor" && (
        <form
          className="inv-form"
          onSubmit={(e) => { e.preventDefault(); handleSaveFlavor(); }}
        >
          <h3 className="inv-form__title">Nuevo sabor</h3>
          <input
            className="inv-input"
            placeholder="Nombre del sabor"
            value={flavorName}
            onChange={(e) => setFlavorName(e.target.value)}
            disabled={saving}
            autoFocus
          />
          <div className="inv-form__actions">
            <button type="submit" className="inv-btn inv-btn--primary" disabled={saving}>
              {saving ? "Guardando…" : (
                <><span className="material-symbols-rounded">save</span> Guardar sabor</>
              )}
            </button>
            <button type="button" className="inv-btn inv-btn--ghost" onClick={resetForm}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* ─── Restock Form ─────────────────────────── */}
      {formMode === "restock" && restockingProduct && (
        <form
          className="inv-form"
          onSubmit={(e) => { e.preventDefault(); handleSaveRestock(); }}
        >
          <h3 className="inv-form__title">
            Restock: {restockingProduct.flavor}
          </h3>
          <div className="inv-form__prices">
            <label>
              <span>Cantidad</span>
              <input
                className="inv-input"
                type="number"
                placeholder="Cantidad"
                value={restockQty}
                onChange={(e) => setRestockQty(e.target.value)}
                min={1}
                disabled={saving}
                autoFocus
              />
            </label>
            <label>
              <span>Costo unitario ($)</span>
              <input
                className="inv-input"
                type="number"
                placeholder="Costo unitario"
                value={restockCost}
                onChange={(e) => setRestockCost(e.target.value)}
                min={0}
                disabled={saving}
              />
            </label>
          </div>
          <div className="inv-form__actions">
            <button type="submit" className="inv-btn inv-btn--primary" disabled={saving}>
              {saving ? "Guardando…" : (
                <><span className="material-symbols-rounded">save</span> Registrar restock</>
              )}
            </button>
            <button type="button" className="inv-btn inv-btn--ghost" onClick={resetForm}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* ─── Models List ──────────────────────────── */}
      {formMode === "none" && models.length === 0 && (
        <p className="inv-empty">Sin modelos aún. Agrega tu primer modelo.</p>
      )}

      {formMode === "none" &&
        models.map((model) => {
          const modelProducts = getProductsByModel(model.id);
          return (
            <div key={model.id} className="inv-model-card">
              <div className="inv-model-card__header">
                <div>
                  <h3 className="inv-model-card__name">{model.name}</h3>
                  <span className="inv-model-card__prices">
                    Costo: ${model.costPrice} → Venta: ${model.sellingPrice}
                  </span>
                </div>
                <div className="inv-model-card__actions">
                  <button
                    className="inv-icon-btn"
                    title="Editar"
                    onClick={() => handleEditModel(model)}
                  >
                    <span className="material-symbols-rounded">edit</span>
                  </button>
                  <button
                    className="inv-icon-btn"
                    title="Eliminar"
                    onClick={() => handleDeleteModel(model.id)}
                  >
                    <span className="material-symbols-rounded">delete</span>
                  </button>
                </div>
              </div>

              {/* Flavor chips */}
              <div className="inv-flavors">
                {modelProducts.map((p) => (
                  <div key={p.id} className="inv-flavor-chip">
                    <span className="inv-flavor-chip__name">{p.flavor}</span>
                    <span className={`inv-flavor-chip__qty ${quantityColor(p.quantity)}`}>
                      {p.quantity}
                    </span>
                    <button
                      className="inv-icon-btn inv-icon-btn--sm"
                      title="Restock"
                      aria-label="Restock"
                      onClick={() => handleRestock(p)}
                    >
                      <span className="material-symbols-rounded">add_shopping_cart</span>
                    </button>
                    <button
                      className="inv-icon-btn inv-icon-btn--sm"
                      title="Eliminar sabor"
                      aria-label="Delete flavor"
                      onClick={() => handleDeleteFlavor(p.id, p.flavor)}
                    >
                      <span className="material-symbols-rounded">close</span>
                    </button>
                  </div>
                ))}
                <button
                  className="inv-flavor-chip inv-flavor-chip--add"
                  aria-label="+ Add flavor"
                  onClick={() => handleAddFlavor(model.id)}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
    </div>
  );
}
